// The `SortBy` component is a dropdown menu for sorting products using Algolia's `useSortBy` hook.
// It allows users to sort items alphabetically (A-Z, Z-A) or by price (Low to High, High to Low).
// The selected sorting option updates both the UI state and Algolia's refinement state. It utilizes
// `headlessui` for the dropdown and `heroicons` for styling. The component ensures a smooth and interactive
// sorting experience with transitions and dynamic text updates.

import { useEffect, useState } from 'react';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon as ArrowIcon } from '@heroicons/react/24/solid';
import { useFormat } from 'helpers/hooks/useFormat';
import { useLocalizedIndex } from 'providers/algolia/localized-index';
import { useSortBy } from 'react-instantsearch-core';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import useLocale from 'helpers/hooks/useLocale';
import { useAccount } from 'frontastic';
import { BaseHit, Hit } from 'instantsearch.js';
interface SortByProps {
  isSearch?: boolean;
}

const SortBy: React.FC<SortByProps> = ({ isSearch }) => {
  const { indexName: productsIndex } = useLocalizedIndex({ type: 'products' });
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { ctLocale: locale } = useLocale();
  const { loggedIn, result } = useAccount();

  const [selectedSort, setSelectedSort] = useState('Please select one');

  const { refine, options, currentRefinement } = useSortBy({
    items: [
      { label: 'A-Z', value: `${productsIndex}_a_z` },
      { label: 'Z-A', value: `${productsIndex}_z_a` },
      { label: 'Low to High', value: `${productsIndex}_price_asc` },
      { label: 'High to Low', value: `${productsIndex}_price_desc` },
    ],
  });

  const handleSortChange = (value: string) => {
    setSelectedSort(value);
    refine(value);
    const parts = value.split('_');
    const keyword = parts.slice(-2).join('_');
    const analytics = new AlgoliaAnalytics(locale);
    const userId = loggedIn
      ? result?.data?.data?.account?.accountId
      : typeof window !== 'undefined' && localStorage.getItem('__anon_id');
    if (isSearch) {
      analytics.handleSearchPageFilterClick(userId, [`sort:${keyword}`]);
    } else {
      analytics.handleFilterClick(userId, [`sort:${keyword}`]);
    }
  };

  useEffect(() => {
    if (currentRefinement) {
      setSelectedSort(currentRefinement);
    }
  }, [currentRefinement]);
  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button>
            <div className="flex items-center gap-8">
              <span className="text-14 text-secondary-black">
                {formatProductMessage({ id: 'sortBy', defaultMessage: 'Sort by:' })}
              </span>
              <span className="text-14 text-secondary-black">
                {selectedSort
                  ? selectedSort === `${productsIndex}_a_z`
                    ? 'Name: A-Z'
                    : selectedSort === `${productsIndex}_z_a`
                    ? 'Name: Z-A'
                    : selectedSort === `${productsIndex}_price_asc`
                    ? 'Price Lowest'
                    : selectedSort === `${productsIndex}_price_desc`
                    ? 'Price Highest'
                    : 'Please select one'
                  : 'Please select one'}
              </span>
              <ArrowIcon className="w-16 self-center stroke-secondary-black" />
            </div>
          </Menu.Button>

          <Menu.Items
            static
            className={`absolute right-0 min-w-[150px] origin-top-right translate-y-[10px] rounded-md border bg-white transition ${
              open ? 'z-20 scale-100' : 'z-[-1] scale-95 opacity-0'
            }`}
          >
            {
              //     [
              //     { label: 'Name: A-Z', value: `${productsIndex}_A-Z` },
              //     { label: 'Name: Z-A', value: `${productsIndex}_Z-A` },
              //     { label: 'Price Lowest', value: `${productsIndex}_price_asc` },
              //     { label: 'Price Highest', value: `${productsIndex}_price_desc` },
              // ]
              options.map(({ label, value }) => (
                <div
                  key={value}
                  onClick={() => handleSortChange(value)}
                  className={`cursor-pointer px-14 py-8 transition hover:bg-blue-500 ${
                    selectedSort === value ? 'bg-neutral-300 font-bold' : 'bg-transparent'
                  }`}
                >
                  {label}
                </div>
              ))
            }
          </Menu.Items>
        </>
      )}
    </Menu>
  );
};

export default SortBy;
